import {useCallback} from 'react';
import {NextPage} from 'next';
import NextLink from 'next/link';
import {useRouter} from 'next/router';

import {useChangeEmail, useReRequestEmailChange} from '#apollo/schema';
import {Button, Screen, Text} from '#component/base';
import {AuthCTAMessages} from '#component/partial';
import {useAuth, useOnceEffect} from '#hook';
import {sprinkles} from '#style/sprinkles';

/**
 * `VerifyEmail` is the page component for the route linked to from email
 * verification emails (ie, 'verifyemail/${token}'). It will attempt to verify
 * the email for the given token, and will conditionally render a success or
 * error result.
 */
export const VerifyEmail: NextPage = () => {
  const {isUserSignedIn, signOut} = useAuth();
  const {query} = useRouter();
  const {token} = query;
  const [changeEmail, {called, data, loading}] = useChangeEmail({
    variables: {input: {token: token as string}},
  });
  const [reRequestChange, {loading: resendLoading}] = useReRequestEmailChange({
    variables: {input: {token: token as string}},
  });
  const authenticated = isUserSignedIn();
  const isLoading = (!!token && !called) || loading;
  const changedEmail = !!data?.changeEmail.success;
  const shouldChangeEmail = !!token && !called && !loading;

  const handleReRequestChange = async () => {
    try {
      await reRequestChange();
    } catch (e) {
      console.error(e);
    }
  };

  const handleChangeEmail = useCallback(async () => {
    try {
      if (shouldChangeEmail) {
        await changeEmail();
        await signOut();
      }
    } catch (e) {
      console.error(e);
    }
  }, [changeEmail, shouldChangeEmail, signOut]);

  useOnceEffect(() => {
    handleChangeEmail();
  });

  return (
    <Screen
      contentContainerClassName={sprinkles({
        flex: 'flex',
        content: ['end', 'end', 'center'],
        dir: 'col',
      })}
      loading={isLoading}
      pageName="Verify email"
      sidePanelImageUrl={true ? '/image/verify-account.jpg' : '/image/verification-error.jpg'}>
      <div
        className={sprinkles({
          direction: 'col',
          flex: 'flex',
          height: 'full',
          placeContent: ['end', 'center'],
        })}>
        {changedEmail ? (
          <>
            <AuthCTAMessages
              title="You’re verified!"
              messages={[
                <div key={1}>
                  <Text as="p" color="doveGray">
                    Thank you for verifying your email.
                  </Text>

                  <Text as="p" color="doveGray">
                    You can now proceed to your account.
                  </Text>
                </div>,
              ]}
            />

            <NextLink href="/signin" passHref>
              <Button as="a" icon="ArrowRight">
                Log in
              </Button>
            </NextLink>
          </>
        ) : (
          <>
            <AuthCTAMessages
              title="We couldn’t verify you"
              messages={[
                <Text as="p" color="doveGray" key={1}>
                  Your verification link expired, but that’s ok. We can resend the verification
                  email with a fresh link to activate your new email.
                </Text>,
              ]}
            />

            {authenticated ? (
              <Button disabled={resendLoading} onClick={handleReRequestChange}>
                Resend email
              </Button>
            ) : (
              <NextLink href="/signin?return_to=/profile" passHref>
                <Button as="a">Log in</Button>
              </NextLink>
            )}
          </>
        )}
      </div>
    </Screen>
  );
};
